/**
 * CRM Patients Async Thunks
 * All async actions for CRM patient operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as crmPatientsApi from '../api/crmPatientsApi';

/**
 * Fetch CRM patients with filters
 */
export const fetchCRMPatientsThunk = createAsyncThunk(
  'crmPatients/fetchPatients',
  async (params, { rejectWithValue }) => {
    try {
      const data = await crmPatientsApi.fetchCRMPatients(params);
      return data;
    } catch (error) {
      console.error('Error fetching CRM patients:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch single CRM patient by ID
 */
export const fetchCRMPatientByIdThunk = createAsyncThunk(
  'crmPatients/fetchPatientById',
  async (id, { rejectWithValue }) => {
    try {
      const data = await crmPatientsApi.fetchCRMPatientById(id);
      return data;
    } catch (error) {
      console.error('Error fetching CRM patient:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Convert lead to patient
 */
export const convertLeadToPatientThunk = createAsyncThunk(
  'crmPatients/convertLeadToPatient',
  async (leadId, { rejectWithValue }) => {
    try {
      const data = await crmPatientsApi.convertLeadToPatient(leadId);
      return data;
    } catch (error) {
      console.error('Error converting lead to patient:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Partial update of a patient profile.
 * Returns { id, updated } so the slice can patch both list and selectedPatient.
 */
export const updatePatientProfileThunk = createAsyncThunk(
  'crmPatients/updatePatient',
  async ({ id, changes }, { rejectWithValue }) => {
    try {
      const updated = await crmPatientsApi.updateCRMPatientProfile(id, changes);
      return { id, updated };
    } catch (error) {
      console.error('Error updating patient profile:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Best-effort lookup of the logged-in user's display name from localStorage.
 * Used as a fallback so notes are never saved with an empty assignee (which
 * would render as "Unassigned" in the UI). Matches the keys other parts of
 * the app (ConsentForms, Updations, MyProfileModal) already read from.
 */
const getCurrentUserName = () => {
  try {
    return (
      localStorage.getItem('userShowName') ||
      localStorage.getItem('user') ||
      ''
    );
  } catch {
    return '';
  }
};

/**
 * Permanently delete a patient profile.
 * Returns the deleted id so the slice can drop it from the list / clear the
 * selected patient if it was the one being viewed.
 */
export const deletePatientProfileThunk = createAsyncThunk(
  'crmPatients/deletePatient',
  async (id, { rejectWithValue }) => {
    try {
      await crmPatientsApi.deleteCRMPatientProfile(id);
      return { id };
    } catch (error) {
      console.error('Error deleting patient profile:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Append a note to a patient.
 * The PUT endpoint replaces fields, so we read current notes from selectedPatient and append.
 *
 * If the caller doesn't supply an `assignee`, we fall back to the current
 * logged-in user's display name so notes never end up "Unassigned".
 */
export const addPatientNoteThunk = createAsyncThunk(
  'crmPatients/addNote',
  async ({ id, note }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const current = state.crmPatients.selectedPatient;
      const existing = Array.isArray(current?.notes) ? current.notes : [];
      const assignee = (note.assignee && note.assignee.trim()) || getCurrentUserName() || 'You';
      const nextNote = {
        text: note.text,
        date: note.date || new Date().toISOString(),
        assignee,
      };
      const updated = await crmPatientsApi.updateCRMPatientProfile(id, {
        notes: [...existing, nextNote],
      });
      return { id, updated };
    } catch (error) {
      console.error('Error adding patient note:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Delete a note from a patient (by index or _id match).
 */
export const deletePatientNoteThunk = createAsyncThunk(
  'crmPatients/deleteNote',
  async ({ id, noteIdOrIndex }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const current = state.crmPatients.selectedPatient;
      const existing = Array.isArray(current?.notes) ? current.notes : [];
      const filtered = existing.filter((n, idx) => {
        if (typeof noteIdOrIndex === 'number') return idx !== noteIdOrIndex;
        return n._id !== noteIdOrIndex;
      });
      const updated = await crmPatientsApi.updateCRMPatientProfile(id, {
        notes: filtered,
      });
      return { id, updated };
    } catch (error) {
      console.error('Error deleting patient note:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
